/*
==================================================
APP TAP SOUND

Функция:
единый проигрыватель звукового отклика интерфейса.

Родитель:
глобальный listener:
src/modules/common/components/app-tap-sound-listener.tsx

Дочерние элементы:
нет

Использует:
src/lib/custom-ui/app-interface-config.ts

Примечания:
- звук создаётся через Web Audio API
- mp3 / wav файлы не используются
- звук запускается только после действия пользователя
- все частоты, громкость и длительности берутся из app-interface-config.ts
- этот файл не должен хранить ручные настройки тональностей
==================================================
*/

import { APP_INTERFACE_SETTINGS } from "../app-interface-config"

export type AppTapSoundType =
  | "default"
  | "category"
  | "addToCart"
  | "remove"
  | "success"
  | "close"

type SoundPatternStep = {
  frequency: number
  delayMs?: number
  durationMs?: number
  volumeMultiplier?: number
  endFrequencyMultiplier?: number
  oscillatorType?: OscillatorType
}

type SoundProfile = {
  volume?: number
  durationMs?: number
  frequencies?: number[]
  oscillatorType?: OscillatorType
  endFrequencyMultiplier?: number
  steps?: SoundPatternStep[]
}

type TapSoundConfig = {
  enabled?: boolean
  defaultEnabled?: boolean
  categoryEnabled?: boolean
  addToCartEnabled?: boolean
  removeEnabled?: boolean
  successEnabled?: boolean
  closeEnabled?: boolean
  volume?: number
  durationMs?: number
  frequencies?: number[]
  profiles?: Partial<Record<AppTapSoundType, SoundProfile>>
}

type ResolvedTapSoundConfig = {
  enabled: boolean
  defaultEnabled: boolean
  categoryEnabled: boolean
  addToCartEnabled: boolean
  removeEnabled: boolean
  successEnabled: boolean
  closeEnabled: boolean
  volume: number
  durationMs: number
  frequencies: number[]
  profiles: Partial<Record<AppTapSoundType, SoundProfile>>
}

let audioContext: AudioContext | null = null
let toneIndex = 0

const DEFAULT_TAP_SOUND_CONFIG: ResolvedTapSoundConfig = {
  enabled: true,
  defaultEnabled: false,
  categoryEnabled: true,
  addToCartEnabled: true,
  removeEnabled: true,
  successEnabled: true,
  closeEnabled: true,
  volume: 0.025,
  durationMs: 55,
  frequencies: [560, 610, 660, 720],
  profiles: {},
}

const getAudioContext = () => {
  if (typeof window === "undefined") return null

  const AudioContextClass =
    window.AudioContext || (window as any).webkitAudioContext

  if (!AudioContextClass) return null

  if (!audioContext) {
    audioContext = new AudioContextClass()
  }

  if (audioContext.state === "suspended") {
    audioContext.resume().catch(() => {
      // Если браузер не дал сразу возобновить AudioContext,
      // интерфейс продолжает работать без ошибки.
    })
  }

  return audioContext
}

const getTapSoundConfig = (): ResolvedTapSoundConfig => {
  const configFromInterface =
    ((APP_INTERFACE_SETTINGS as any).tapSound || {}) as TapSoundConfig

  return {
    enabled:
      typeof configFromInterface.enabled === "boolean"
        ? configFromInterface.enabled
        : DEFAULT_TAP_SOUND_CONFIG.enabled,
    defaultEnabled:
      typeof configFromInterface.defaultEnabled === "boolean"
        ? configFromInterface.defaultEnabled
        : DEFAULT_TAP_SOUND_CONFIG.defaultEnabled,
    categoryEnabled:
      typeof configFromInterface.categoryEnabled === "boolean"
        ? configFromInterface.categoryEnabled
        : DEFAULT_TAP_SOUND_CONFIG.categoryEnabled,
    addToCartEnabled:
      typeof configFromInterface.addToCartEnabled === "boolean"
        ? configFromInterface.addToCartEnabled
        : DEFAULT_TAP_SOUND_CONFIG.addToCartEnabled,
    removeEnabled:
      typeof configFromInterface.removeEnabled === "boolean"
        ? configFromInterface.removeEnabled
        : DEFAULT_TAP_SOUND_CONFIG.removeEnabled,
    successEnabled:
      typeof configFromInterface.successEnabled === "boolean"
        ? configFromInterface.successEnabled
        : DEFAULT_TAP_SOUND_CONFIG.successEnabled,
    closeEnabled:
      typeof configFromInterface.closeEnabled === "boolean"
        ? configFromInterface.closeEnabled
        : DEFAULT_TAP_SOUND_CONFIG.closeEnabled,
    volume:
      typeof configFromInterface.volume === "number"
        ? configFromInterface.volume
        : DEFAULT_TAP_SOUND_CONFIG.volume,
    durationMs:
      typeof configFromInterface.durationMs === "number"
        ? configFromInterface.durationMs
        : DEFAULT_TAP_SOUND_CONFIG.durationMs,
    frequencies: Array.isArray(configFromInterface.frequencies)
      ? configFromInterface.frequencies
      : DEFAULT_TAP_SOUND_CONFIG.frequencies,
    profiles:
      configFromInterface.profiles && typeof configFromInterface.profiles === "object"
        ? configFromInterface.profiles
        : DEFAULT_TAP_SOUND_CONFIG.profiles,
  }
}

const isSoundTypeEnabled = (
  type: AppTapSoundType,
  config: ResolvedTapSoundConfig
) => {
  if (!config.enabled) return false

  if (type === "category") return config.categoryEnabled
  if (type === "addToCart") return config.addToCartEnabled
  if (type === "remove") return config.removeEnabled
  if (type === "success") return config.successEnabled
  if (type === "close") return config.closeEnabled

  return config.defaultEnabled
}

const getDefaultFrequency = (frequencies: number[]) => {
  const safeFrequencies = frequencies.length
    ? frequencies
    : DEFAULT_TAP_SOUND_CONFIG.frequencies

  const frequency = safeFrequencies[toneIndex % safeFrequencies.length]
  toneIndex += 1
  return frequency
}

const getProfile = (
  type: AppTapSoundType,
  config: ResolvedTapSoundConfig
): SoundProfile => {
  return config.profiles[type] || config.profiles.default || {}
}

const getSoundPattern = (
  type: AppTapSoundType,
  config: ResolvedTapSoundConfig
): SoundPatternStep[] => {
  const profile = getProfile(type, config)

  if (Array.isArray(profile.steps) && profile.steps.length > 0) {
    return profile.steps
  }

  const frequencies = profile.frequencies || config.frequencies
  const defaultFrequency = getDefaultFrequency(frequencies)

  return [
    {
      frequency: defaultFrequency,
      durationMs: profile.durationMs || config.durationMs,
      volumeMultiplier: 1,
      endFrequencyMultiplier: profile.endFrequencyMultiplier || 1,
      oscillatorType: profile.oscillatorType || "sine",
    },
  ]
}

const getProfileVolume = (
  type: AppTapSoundType,
  config: ResolvedTapSoundConfig
) => {
  const profile = getProfile(type, config)
  return typeof profile.volume === "number" ? profile.volume : config.volume
}

const getProfileDuration = (
  type: AppTapSoundType,
  config: ResolvedTapSoundConfig
) => {
  const profile = getProfile(type, config)
  return typeof profile.durationMs === "number"
    ? profile.durationMs
    : config.durationMs
}

const playToneStep = (
  ctx: AudioContext,
  step: SoundPatternStep,
  type: AppTapSoundType,
  config: ResolvedTapSoundConfig
) => {
  const delay = (step.delayMs || 0) / 1000
  const duration = (step.durationMs || getProfileDuration(type, config)) / 1000
  const volume = getProfileVolume(type, config) * (step.volumeMultiplier || 1)

  const startTime = ctx.currentTime + delay
  const endTime = startTime + duration
  const endFrequency = step.frequency * (step.endFrequencyMultiplier || 1)

  const oscillator = ctx.createOscillator()
  const gain = ctx.createGain()

  oscillator.type = step.oscillatorType || "sine"
  oscillator.frequency.setValueAtTime(step.frequency, startTime)
  oscillator.frequency.exponentialRampToValueAtTime(endFrequency, endTime)

  gain.gain.setValueAtTime(0.0001, startTime)
  gain.gain.exponentialRampToValueAtTime(volume, startTime + 0.008)
  gain.gain.exponentialRampToValueAtTime(0.0001, endTime)

  oscillator.connect(gain)
  gain.connect(ctx.destination)

  oscillator.start(startTime)
  oscillator.stop(endTime)
}

export const playAppTapSound = (type: AppTapSoundType = "default") => {
  const soundConfig = getTapSoundConfig()

  if (!isSoundTypeEnabled(type, soundConfig)) return

  const ctx = getAudioContext()
  if (!ctx) return

  const pattern = getSoundPattern(type, soundConfig)

  pattern.forEach((step) => {
    playToneStep(ctx, step, type, soundConfig)
  })
}
